import { cloneDeep, floor, toInteger } from 'lodash';
import { IImageLayerProps, ILayerProps } from '@/version-types';
import { isImageOutOfRangeLayer } from './geometry';
import { computerImageLayerRatio } from './layer-ratio';

export const rotateImage = (layer: ILayerProps, rotate: number, originScale?: number) => {
    const newLayer = cloneDeep(layer);
    const { imageTransform } = newLayer.data as IImageLayerProps;
    imageTransform.rotate = rotate;

    const isOutAndScaleLayer = (outLayer: ILayerProps, isZoomOut: boolean = false) => {
        const isOut = isImageOutOfRangeLayer(outLayer);
        const { width, height, data } = outLayer;
        const { imageTransform: transform } = data as IImageLayerProps;
        const { offsetX, offsetY, widthRatio, heightRatio, scale: imageScale } = transform;
        const imageWidth = width * widthRatio * imageScale;
        const imageHeight = height * heightRatio * imageScale;
        const imageOffsetX = offsetX * widthRatio * width * imageScale;
        const imageOffsetY = offsetY * heightRatio * height * imageScale;

        if (isOut && !isZoomOut) {
            const newImageWidth =
                imageWidth < imageHeight ? imageWidth + 1 : (imageHeight + 1) / (imageHeight / imageWidth);
            const newImageHeight =
                imageHeight < imageWidth ? imageHeight + 1 : (imageWidth + 1) / (imageWidth / imageHeight);
            const newImageScale = newImageWidth / (width * widthRatio);
            transform.scale = newImageScale;
            transform.offsetX = imageOffsetX / newImageWidth;
            transform.offsetY = imageOffsetY / newImageHeight;
            isOutAndScaleLayer(outLayer);
        } else if (!isOut && originScale && originScale < imageScale) {
            const oldLayer = cloneDeep(outLayer);
            const newImageWidth =
                imageWidth > imageHeight ? imageWidth - 1 : (imageHeight - 1) / (imageHeight / imageWidth);
            const newImageHeight =
                imageHeight > imageWidth ? imageHeight - 1 : (imageWidth - 1) / (imageWidth / imageHeight);
            const newImageScale = newImageWidth / (width * widthRatio);
            transform.scale = newImageScale;
            transform.offsetX = imageOffsetX / newImageWidth;
            transform.offsetY = imageOffsetY / newImageHeight;

            if (!isImageOutOfRangeLayer(outLayer)) {
                isOutAndScaleLayer(outLayer, true);
            } else {
                Object.assign(outLayer, oldLayer);
            }
        }
    };

    isOutAndScaleLayer(newLayer);
    return newLayer;
};

export const adjustImageLayerRatio = (layer: ILayerProps, ratio: string) => {
    const { width, height, data } = layer;
    const { imageTransform } = data as IImageLayerProps;
    const { widthRatio, heightRatio, scale: imageScale } = imageTransform;
    const originImageWidth = width * widthRatio * imageScale;
    const originImageHeight = height * heightRatio * imageScale;
    const originMaxWidth = Math.max(width, height);

    let newLayerWidth = width;
    let newLayerHeight = height;
    const newRatio = ratio.split(':');
    const newLayerWidthRatio = parseInt(newRatio[0]);
    const newLayerHeightRatio = parseInt(newRatio[1]);
    if (newLayerWidthRatio > newLayerHeightRatio) {
        newLayerWidth = originMaxWidth;
        newLayerHeight = (newLayerWidth / newLayerWidthRatio) * newLayerHeightRatio;
    } else {
        newLayerHeight = originMaxWidth;
        newLayerWidth = (newLayerHeight / newLayerHeightRatio) * newLayerWidthRatio;
    }

    const {
        widthRatio: newWidthRatio,
        heightRatio: newHeightRatio,
        scale: newScale,
    } = computerImageLayerRatio(newLayerWidth, newLayerHeight, originImageWidth, originImageHeight);

    const newLayer = cloneDeep(layer);
    const { imageTransform: newImageTransform } = newLayer.data as IImageLayerProps;
    newImageTransform.widthRatio = newWidthRatio;
    newImageTransform.heightRatio = newHeightRatio;
    newImageTransform.scale = newScale;
    newLayer.width = toInteger(newLayerWidth);
    newLayer.height = toInteger(newLayerHeight);

    const isOutAndScaleLayer = (outLayer: ILayerProps) => {
        if (!isImageOutOfRangeLayer(outLayer)) return;
        const { width: w, height: h, data: d } = outLayer;
        const { imageTransform: t } = d as IImageLayerProps;
        const imageWidth = w * t.widthRatio * t.scale;
        const imageHeight = h * t.heightRatio * t.scale;
        const newWidth = w < h ? w - 1 : floor((h - 1) / (h / w));
        const newHeight = h < w ? h - 1 : floor((w - 1) / (w / h));
        const ratios = computerImageLayerRatio(newWidth, newHeight, imageWidth, imageHeight);
        t.widthRatio = ratios.widthRatio;
        t.heightRatio = ratios.heightRatio;
        t.scale = ratios.scale;
        outLayer.width = floor(newWidth);
        outLayer.height = floor(newHeight);
        isOutAndScaleLayer(outLayer);
    };

    isOutAndScaleLayer(newLayer);
    newLayer.left = layer.left + (layer.width - newLayer.width) / 2;
    newLayer.top = layer.top + (layer.height - newLayer.height) / 2;
    return newLayer;
};

export const isLayerSizeRadio = (width: number, height: number) => {
    const ratio = width / height;
    if (width === height) return '1:1';
    if (ratio === 16 / 9) return '16:9';
    if (ratio === 9 / 16) return '9:16';
    if (ratio === 4 / 3) return '4:3';
    if (ratio === 3 / 4) return '3:4';
    if (ratio === 3 / 2) return '3:2';
    if (ratio === 2 / 3) return '2:3';
    return false;
};
